import { API_BASE_URL } from "../../../../config/api";
import { getCompany } from "../../../../shared/data/storage/authStorage";

// Helper function to get auth token
const getToken = (): string | null => {
  return localStorage.getItem("token");
};

// API Error Class
class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

export const getStoredCompanyId = (): number => {
  const rawCompanyId = getCompany()?.companyId;
  const companyId = rawCompanyId
    ? Number.parseInt(String(rawCompanyId), 10)
    : NaN;

  if (!Number.isFinite(companyId) || companyId <= 0) {
    throw new APIError("Company details not found. Please sign in again.", 400);
  }

  return companyId;
};

// Upload mapping response types
export interface UploadMappingResponse {
  success: boolean;
  phase: "validation" | "processing";
  message: string;
  data: {
    total_rows: number;
    processed?: number;
    valid_rows?: number;
    invalid_rows?: number;
    new_gls?: number;
    updated_gls?: number;
    errors?: UploadError[];
  };
}

export interface UploadError {
  row: number;
  ledger_name: string;
  field?: string;
  error: string;
  error_code: string;
}

/**
 * Upload MCOA mapping Excel file
 * @param file Excel file to upload
 * @returns Upload response with summary
 */
export const uploadMapping = async (
  file: File,
): Promise<UploadMappingResponse> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("company_id", String(getStoredCompanyId()));

  const response = await fetch(
    `${API_BASE_URL}/api/trial-balance/upload-mapping`,
    {
      method: "POST",
      body: formData,
    },
  );

  const data = await response.json();

  // Return the response directly (even if not ok) if it has the expected structure
  // This allows us to show validation errors on the page
  if (data.phase && data.data !== undefined) {
    return data as UploadMappingResponse;
  }

  // If response is not ok and doesn't have expected structure, throw error
  if (!response.ok) {
    throw new APIError(
      data.message || `Upload failed: ${response.statusText}`,
      response.status,
      data,
    );
  }

  return data as UploadMappingResponse;
};

/**
 * Upload Manual MCOA mapping Excel file (without Zoho Account ID)
 * @param file Excel file to upload
 * @returns Upload response with summary
 */
export const uploadManualMapping = async (
  file: File,
): Promise<UploadMappingResponse> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("company_id", String(getStoredCompanyId()));

  const response = await fetch(
    `${API_BASE_URL}/api/trial-balance/upload-manual-mapping`,
    {
      method: "POST",
      body: formData,
    },
  );

  const data = await response.json();

  // Return the response directly (even if not ok) if it has the expected structure
  // This allows us to show validation errors on the page
  if (data.phase && data.data !== undefined) {
    return data as UploadMappingResponse;
  }

  // If response is not ok and doesn't have expected structure, throw error
  if (!response.ok) {
    throw new APIError(
      data.message || `Upload failed: ${response.statusText}`,
      response.status,
      data,
    );
  }

  return data as UploadMappingResponse;
};

// Trial Balance upload response types
export interface UploadTrialBalanceResponse {
  success: boolean;
  phase: "validation" | "uploaded";
  message: string;
  data: {
    total_rows: number;
    valid_rows?: number;
    invalid_rows?: number;
    duplicate_in_file?: number;
    duplicate_in_db?: number;
    uploaded?: number;
    month?: string;
    year?: number;
    month_number?: number;
    months?: Array<{
      header: string;
      year: number;
      month_number: number;
    }>;
    total_months?: number;
    has_existing_data?: boolean;
    warning_message?: string;
    existing_months?: Array<{
      header: string;
      year: number;
      month_number: number;
      record_count: number;
    }>;
    total_deleted?: number;
    deleted_counts?: { [monthHeader: string]: number };
    records_per_month?: { [monthHeader: string]: number };
    months_processed?: string[];
    preview?: Array<{
      row: number;
      ledger_name: string;
      amount: number;
    }>;
    errors?: UploadError[];
  };
}

/**
 * Upload Trial Balance Excel file (validation phase)
 * @param file Excel file to upload
 * @returns Upload response with validation results
 */
export const uploadTrialBalance = async (
  file: File,
  phase: "validation" | "confirm" = "validation",
): Promise<UploadTrialBalanceResponse> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("phase", phase);
  formData.append("company_id", String(getStoredCompanyId()));

  const response = await fetch(
    `${API_BASE_URL}/api/trial-balance/upload-trial-balance`,
    {
      method: "POST",
      body: formData,
    },
  );

  const data = await response.json();

  // Return the response directly (even if not ok) if it has the expected structure
  if (data.phase && data.data !== undefined) {
    return data as UploadTrialBalanceResponse;
  }

  // If response is not ok and doesn't have expected structure, throw error
  if (!response.ok) {
    throw new APIError(
      data.message || `Upload failed: ${response.statusText}`,
      response.status,
      data,
    );
  }

  return data as UploadTrialBalanceResponse;
};

// Report generation types
export interface GenerateReportParams {
  company_id?: number;
  from_date: string; // YYYY-MM-DD
  to_date: string; // YYYY-MM-DD
  group_by: "monthwise" | "quarterly" | "yearly" | "6months";
  save_report?: boolean;
  tag_filters?: {
    [tagName: string]: string[]; // e.g., { "T1": ["Tag1", "Tag2"], "T2": [] }
  };
}

export interface AvailableTagsResponse {
  success: boolean;
  message: string;
  data: {
    [tagName: string]: string[]; // e.g., { "T1": ["Tag1", "Tag2"], "T2": ["TagA", "TagB"] }
  };
}

export interface GenerateReportResponse {
  success: boolean;
  message: string;
  data: {
    report_id?: number;
    report_name: string;
    file_name: string;
    file_path: string;
    download_url: string;
    from_date: string;
    to_date: string;
    group_by: string;
    total_gls: number;
    periods: number;
  };
}

export interface SavedReport {
  id: number;
  company_id: number;
  report_name: string;
  from_date: string;
  to_date: string;
  group_by: string;
  tag_filters?: string | null; // JSON string of applied tag filters
  file_path: string;
  file_name: string;
  created_at: string;
  updated_at: string;
}

export interface GetReportsResponse {
  success: boolean;
  message: string;
  data: {
    reports: SavedReport[];
    total: number;
  };
}

// Register check types
export interface CheckRegistersParams {
  company_id?: number;
  from_date: string;
  to_date: string;
  group_by?: "monthwise" | "quarterly" | "yearly" | "6months";
}

export interface PeriodStatus {
  period_key: string;
  display: string;
  year: number;
  month_number: number;
  has_register: boolean;
}

export interface RegisterStatus {
  register_id: number;
  register_name: string;
  l2_level_id: number;
  periods: PeriodStatus[];
  summary: {
    total_periods: number;
    with_registers: number;
    without_registers: number;
    uploaded_months: string[];
    missing_months: string[];
  };
}

export interface MonthSummary {
  period_key: string;
  display: string;
}

export interface MonthWiseSummary {
  available_months: MonthSummary[];
  missing_months: MonthSummary[];
  total_available: number;
  total_missing: number;
  total_periods: number;
}

export interface CheckRegistersResponse {
  success: boolean;
  message: string;
  data: {
    registers: RegisterStatus[];
    overall_summary: {
      total_leads_with_registers: number; // Total leads that have registers uploaded (anywhere)
      leads_with_all_months: number; // Leads with registers for ALL report months
      leads_with_some_months: number; // Leads with registers for SOME report months
      leads_with_no_months: number; // Leads with registers but NONE for report months
    };
    month_wise_summary: MonthWiseSummary;
  };
}

/**
 * Get available tags and their distinct values for a company
 * @param companyId Company ID (defaults to stored company)
 * @returns Available tags grouped by tag name
 */
export const getAvailableTags = async (
  companyId: number = getStoredCompanyId(),
): Promise<AvailableTagsResponse> => {
  const response = await fetch(
    `${API_BASE_URL}/api/trial-balance/available-tags?company_id=${companyId}`,
  );

  const data = await response.json();

  if (!response.ok) {
    throw new APIError(
      data.message ||
        `Failed to retrieve available tags: ${response.statusText}`,
      response.status,
      data,
    );
  }

  return data as AvailableTagsResponse;
};

/**
 * Generate Trial Balance Report
 * @param params Report generation parameters
 * @returns Report generation response with download URL
 */
export const generateReport = async (
  params: GenerateReportParams,
): Promise<GenerateReportResponse> => {
  const formData = new FormData();
  formData.append("company_id", String(getStoredCompanyId()));
  formData.append("from_date", params.from_date);
  formData.append("to_date", params.to_date);
  formData.append("group_by", params.group_by);
  formData.append("save_report", String(params.save_report ?? true));

  // Add tag filters if provided
  if (params.tag_filters && Object.keys(params.tag_filters).length > 0) {
    // Only include tags with non-empty arrays (empty = Select All = no filter)
    const activeFilters: { [key: string]: string[] } = {};
    for (const [tagName, values] of Object.entries(params.tag_filters)) {
      if (Array.isArray(values) && values.length > 0) {
        activeFilters[tagName] = values;
      }
    }

    if (Object.keys(activeFilters).length > 0) {
      formData.append("tag_filters", JSON.stringify(activeFilters));
    }
  }

  const response = await fetch(
    `${API_BASE_URL}/api/trial-balance/generate-report`,
    {
      method: "POST",
      body: formData,
    },
  );

  const data = await response.json();

  if (!response.ok) {
    throw new APIError(
      data.message || `Report generation failed: ${response.statusText}`,
      response.status,
      data,
    );
  }

  return data as GenerateReportResponse;
};

/**
 * Get effective date info (for demo mode support)
 * @returns Effective date information including year, month, and demo mode status
 */
export interface EffectiveDateResponse {
  success: boolean;
  message: string;
  data: {
    effective_date: string; // YYYY-MM-DD
    effective_year: number;
    effective_month: number; // 1-12
    is_demo_mode: boolean;
    /** Latest books-closed month, YYYY-MM (null if none). */
    latest_closed_month?: string | null;
    /** Long label for latest closed month, e.g. "March 2025" (null if none). */
    latest_closed_month_label?: string | null;
    /** FY start month for latest closed month, YYYY-MM (null if none). */
    fy_start_month?: string | null;
    /** FY range label, e.g. "Apr 2024 – Mar 2025" (null if none). */
    fy_range_label?: string | null;
  };
}

export const getEffectiveDateInfo =
  async (): Promise<EffectiveDateResponse> => {
    const queryParams = new URLSearchParams({
      company_id: String(getStoredCompanyId()),
    });
    const response = await fetch(
      `${API_BASE_URL}/api/trial-balance/effective-date?${queryParams.toString()}`,
    );

    const data = await response.json();

    if (!response.ok) {
      throw new APIError(
        data.message ||
          `Failed to retrieve effective date: ${response.statusText}`,
        response.status,
        data,
      );
    }

    return data as EffectiveDateResponse;
  };

/**
 * Balance sheet summary cards (Cash & Bank, Working Capital, Deferred Revenue, ratios).
 * GET /api/trial-balance/balance-sheet-summary?end_month=YYYY-MM
 */
export type BalanceSheetSummaryTrend = "up" | "down" | "neutral";

export interface BalanceSheetSummaryCard {
  key: string;
  label: string;
  value: number;
  value_formatted: string;
  previous_value: number;
  trend: BalanceSheetSummaryTrend;
  trend_text: string;
  change_percent: number | null;
}

export interface BalanceSheetSummaryAsOf {
  end_month: string;
  year: number;
  month: number;
  label: string;
}

export interface BalanceSheetSummaryResponse {
  success: boolean;
  message: string;
  data: {
    period: {
      end_month: string;
      year: number;
      month: number;
    };
    as_of?: BalanceSheetSummaryAsOf;
    cards: BalanceSheetSummaryCard[];
  };
}

export const getBalanceSheetSummary = async (params?: {
  end_month?: string;
  company_id?: number;
}): Promise<BalanceSheetSummaryResponse> => {
  const searchParams = new URLSearchParams();
  if (params?.end_month) searchParams.set("end_month", params.end_month);
  searchParams.set(
    "company_id",
    String(params?.company_id ?? getStoredCompanyId()),
  );

  const query = searchParams.toString();
  const url = `${API_BASE_URL}/api/trial-balance/balance-sheet-summary${query ? `?${query}` : ""}`;
  const response = await fetch(url);
  const data = await response.json();
  if (!response.ok) {
    throw new APIError(
      data.message || "Failed to load balance sheet summary",
      response.status,
      data,
    );
  }
  return data as BalanceSheetSummaryResponse;
};

/**
 * P&L summary from client_monthly_data (YTD Revenue, EBITDA, Margin, PAT).
 */
export interface PlSummaryResponse {
  success: boolean;
  message: string;
  data: {
    ytd_revenue: number;
    ytd_revenue_prev_available: boolean;
    ytd_revenue_prev: number | null;
    ytd_revenue_change_pct: number | null;
    ytd_ebitda: number;
    ytd_ebitda_prev_available: boolean;
    ytd_ebitda_prev: number | null;
    ytd_ebitda_change_pct: number | null;
    ebitda_margin: number | null;
    ebitda_margin_prev_available: boolean;
    ebitda_margin_prev: number | null;
    /** EBITDA margin delta vs prior FY, in percentage points (pp). */
    ebitda_margin_change_pp: number | null;
    pat: number;
    pat_prev_available: boolean;
    pat_prev: number | null;
    pat_change_pct: number | null;
    ytd_subtext: string;
  };
}

export const getPlSummary = async (): Promise<PlSummaryResponse> => {
  const response = await fetch(
    `${API_BASE_URL}/api/client-profit-and-loss/summary`,
  );
  const data = await response.json();
  if (!response.ok) {
    throw new APIError(
      data.message || "Failed to load P&L summary",
      response.status,
      data,
    );
  }
  return data as PlSummaryResponse;
};

/**
 * P&L Summary AI insights (3 items with accentColor + description).
 */
export type PlSummaryInsightAccent = "critical" | "warning" | "neutral";

export interface PlSummaryInsightItem {
  accentColor: PlSummaryInsightAccent;
  description: string;
}

export interface PlSummaryInsightsResponse {
  success: boolean;
  message?: string;
  data: {
    insights: PlSummaryInsightItem[];
  };
}

export const getPlSummaryInsights =
  async (): Promise<PlSummaryInsightsResponse> => {
    const response = await fetch(`${API_BASE_URL}/api/cfo/pl_summary_insights`);
    const data = await response.json();
    if (!response.ok) {
      throw new APIError(
        data.message || "Failed to load P&L insights",
        response.status,
        data,
      );
    }
    return data as PlSummaryInsightsResponse;
  };

export type BalanceSheetInsightBulletColor = "error" | "warning" | "neutral";

export interface BalanceSheetSummaryInsightItem {
  bulletColor: BalanceSheetInsightBulletColor;
  text: string;
}

export interface BalanceSheetSummaryInsightsResponse {
  success: boolean;
  message?: string;
  data: {
    insights: BalanceSheetSummaryInsightItem[];
  };
}

/** GET /api/cfo/balance_sheet_summary_insights — effective period from server; no company_id */
export const getBalanceSheetSummaryInsights =
  async (): Promise<BalanceSheetSummaryInsightsResponse> => {
    const response = await fetch(
      `${API_BASE_URL}/api/cfo/balance_sheet_summary_insights`,
    );
    const data = await response.json();
    if (!response.ok) {
      throw new APIError(
        data.message || "Failed to load balance sheet insights",
        response.status,
        data,
      );
    }
    return data as BalanceSheetSummaryInsightsResponse;
  };

/**
 * Get saved reports for a company
 * @param companyId Company ID (defaults to stored company)
 * @param limit Maximum number of reports to return
 * @returns List of saved reports
 */
export const getReports = async (
  companyId: number = getStoredCompanyId(),
  limit: number = 50,
): Promise<GetReportsResponse> => {
  const response = await fetch(
    `${API_BASE_URL}/api/trial-balance/reports?company_id=${companyId}&limit=${limit}`,
  );

  const data = await response.json();

  if (!response.ok) {
    throw new APIError(
      data.message || `Failed to retrieve reports: ${response.statusText}`,
      response.status,
      data,
    );
  }

  return data as GetReportsResponse;
};

/**
 * Download a report file
 * @param reportId Report ID (or 'temp' for temporary reports)
 * @param fileName File name
 */
export const downloadReport = (
  reportId: string | number,
  fileName: string,
): void => {
  const url = `${API_BASE_URL}/api/trial-balance/download-report/${reportId}/${encodeURIComponent(fileName)}`;
  window.open(url, "_blank");
};

// Excel Viewer Types
export interface CellBorderSide {
  style: string;
  color: string;
}

export interface CellBorders {
  top?: CellBorderSide;
  bottom?: CellBorderSide;
  left?: CellBorderSide;
  right?: CellBorderSide;
}

export interface CellStyle {
  fontWeight?: string;
  fontStyle?: string;
  textDecoration?: string;
  color?: string;
  fontSize?: string;
  backgroundColor?: string;
  textAlign?: string;
  verticalAlign?: string;
  borders?: CellBorders;
  numberFormat?: string;
}

export interface ExcelCell {
  col: string;
  row: number;
  value: string | number | null;
  formula: string | null;
  style?: CellStyle;
}

export interface ExcelSheet {
  rows: ExcelCell[][];
  maxCol: string;
  maxRow: number;
  mergedCells?: string[];
  columnWidths?: Record<string, number>;
  rowHeights?: Record<number, number>;
}

export interface ParsedExcelData {
  fileName: string;
  reportName: string;
  sheetNames: string[];
  sheets: { [sheetName: string]: ExcelSheet };
}

export interface ParseReportResponse {
  success: boolean;
  message: string;
  data: ParsedExcelData;
}

export interface TrialBalanceTransaction {
  id: number;
  zoho_transaction_id?: string | null;
  zoho_account_id?: string | null;
  categorized_transaction_id?: string | null;
  transaction_type?: string | null;
  transaction_status?: string | null;
  transaction_source?: string | null;
  transaction_date?: string | null;
  entry_number?: string | null;
  reference_number?: string | null;
  payee?: string | null;
  customer_id?: string | null;
  description?: string | null;
  debit_or_credit?: string | null;
  debit_amount?: number | null;
  credit_amount?: number | null;
  currency_code?: string | null;
  offset_account_name?: string | null;
  view_document_id?: number | null;
  view_document_type?: "invoice" | "bill" | null;
}

export interface LedgerTransactionsResponse {
  success: boolean;
  message: string;
  data: {
    ledger_name: string;
    ledger_name_matched?: string | null;
    class_1?: string | null;
    mcoa_id?: number | null;
    mcoa_ids?: number[];
    zoho_account_id?: string | null;
    zoho_account_ids?: string[];
    ambiguous_match?: boolean;
    year: number;
    month_number: number;
    from_date: string;
    to_date: string;
    opening_balance?: number | null;
    closing_balance?: number | null;
    transactions: TrialBalanceTransaction[];
  };
  /** Present when request included debug=1; use for troubleshooting "no transactions" */
  debug?: {
    input?: Record<string, unknown>;
    formula_parsed?: { criteria: unknown; criteria_empty: boolean };
    mcoa_from_formula?: {
      id: unknown;
      ledger_name: unknown;
      zoho_account_id: unknown;
    } | null;
    source?: string;
    trial_balance_lookup?: Record<string, unknown>;
    transaction_lookup?: {
      zoho_account_id: string;
      from_date: string;
      to_date: string;
      transactions_in_range: number;
      total_transactions_for_account: number;
    };
  };
}

/**
 * Parse report Excel file for viewer
 * Returns all sheets with cell data and formulas
 * @param reportId Report ID
 * @returns Parsed Excel data with all sheets
 */
export const parseReportForViewer = async (
  reportId: number,
): Promise<ParseReportResponse> => {
  const response = await fetch(
    `${API_BASE_URL}/api/trial-balance/reports/${reportId}/parse`,
  );

  const data = await response.json();

  if (!response.ok) {
    throw new APIError(
      data.message || `Failed to parse report: ${response.statusText}`,
      response.status,
      data,
    );
  }

  return data as ParseReportResponse;
};

/**
 * Get account transactions for a Trial Balance ledger and month
 */
export const getLedgerTransactions = async (params: {
  ledger_name?: string;
  formula?: string;
  year: number;
  month_number: number;
  class_1?: string;
  company_id?: number;
  debug?: boolean;
}): Promise<LedgerTransactionsResponse> => {
  const queryParams = new URLSearchParams();
  if (params.ledger_name) {
    queryParams.append("ledger_name", params.ledger_name);
  }
  if (params.formula) {
    queryParams.append("formula", params.formula);
  }
  queryParams.append("year", String(params.year));
  queryParams.append("month_number", String(params.month_number));
  if (params.class_1) {
    queryParams.append("class_1", params.class_1);
  }
  if (params.company_id) {
    queryParams.append("company_id", String(params.company_id));
  } else {
    queryParams.append("company_id", String(getStoredCompanyId()));
  }
  if (params.debug) {
    queryParams.append("debug", "1");
  }

  const response = await fetch(
    `${API_BASE_URL}/api/trial-balance/ledger-transactions?${queryParams.toString()}`,
  );
  const data = await response.json();

  if (!response.ok) {
    throw new APIError(
      data.message || `Failed to fetch transactions: ${response.statusText}`,
      response.status,
      data,
    );
  }

  return data as LedgerTransactionsResponse;
};

/**
 * Check register availability for date range
 * @param params Check parameters
 * @returns Register status for all registers
 */
export const checkRegistersAvailability = async (
  params: CheckRegistersParams,
): Promise<CheckRegistersResponse> => {
  const queryParams = new URLSearchParams();
  queryParams.append("company_id", String(getStoredCompanyId()));
  queryParams.append("from_date", params.from_date);
  queryParams.append("to_date", params.to_date);
  if (params.group_by) {
    queryParams.append("group_by", params.group_by);
  }

  const response = await fetch(
    `${API_BASE_URL}/api/trial-balance/check-registers?${queryParams.toString()}`,
  );

  const data = await response.json();

  if (!response.ok) {
    throw new APIError(
      data.message || `Failed to check registers: ${response.statusText}`,
      response.status,
      data,
    );
  }

  return data as CheckRegistersResponse;
};

// GL Mapping Statistics types
export interface GLMappingStats {
  total_gls: number;
  synced_from_zoho: number;
  without_classification: number;
  without_levels: number;
  manual_uploads: number;
}

export interface GLMappingStatsResponse {
  success: boolean;
  data: GLMappingStats;
  message?: string;
}

/**
 * Fetch GL Mapping Statistics
 * @returns Statistics about GL mappings
 * No authentication required - public endpoint
 */
/**
 * Download Trial Balance data for a specific month
 * @param year Year (e.g., 2025)
 * @param monthNumber Month number (1-12)
 * @param companyId Company ID (defaults to stored company)
 */
export const downloadTrialBalanceData = async (
  year: number,
  monthNumber: number,
  companyId: number = getStoredCompanyId(),
): Promise<void> => {
  const url = `${API_BASE_URL}/api/trial-balance/download-trial-balance?company_id=${companyId}&year=${year}&month_number=${monthNumber}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new APIError(
      errorData.message ||
        `Failed to download trial balance: ${response.statusText}`,
      response.status,
    );
  }

  // Get filename from Content-Disposition header or generate
  const contentDisposition = response.headers.get("Content-Disposition");
  let filename = `trial_balance_${year}_${monthNumber}.xlsx`;
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }

  // Download file
  const blob = await response.blob();
  const url_blob = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url_blob;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url_blob);
};

export const fetchGLMappingStats =
  async (): Promise<GLMappingStatsResponse> => {
    const queryParams = new URLSearchParams({
      company_id: String(getStoredCompanyId()),
    });
    const response = await fetch(
      `${API_BASE_URL}/api/trial-balance/gl-mapping-stats?${queryParams.toString()}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const data = await response.json();

    if (!response.ok) {
      throw new APIError(
        data.message ||
          `Failed to fetch GL mapping stats: ${response.statusText}`,
        response.status,
        data,
      );
    }

    return data as GLMappingStatsResponse;
  };

// Delete GLs types
export interface GLListItem {
  id: number;
  ledger_name: string;
  class_1: string;
  class_2: string;
  account_id: string;
  tb_entries_count: number;
  has_tb_entries: boolean;
  created_at: string;
}

export interface GetGlsListResponse {
  success: boolean;
  data: GLListItem[];
  total: number;
  message?: string;
}

export interface DeleteGlsParams {
  gl_ids: number[];
  company_id?: number;
}

export interface DeletedGL {
  id: number;
  ledger_name: string;
  tb_entries_count: number;
}

export interface DeleteGlsResponse {
  success: boolean;
  message: string;
  data: {
    deleted_count: number;
    tb_entries_deleted: number;
    levels_deleted: number;
    tags_deleted: number;
    deleted_gls: DeletedGL[];
  };
}

/**
 * Get list of GLs with Trial Balance entry counts
 * @param companyId Company ID (defaults to stored company)
 * @returns List of GL accounts with TB counts
 */
export const getGlsList = async (
  companyId: number = getStoredCompanyId(),
): Promise<GetGlsListResponse> => {
  const response = await fetch(
    `${API_BASE_URL}/api/trial-balance/gls-list?company_id=${companyId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  const data = await response.json();

  if (!response.ok) {
    throw new APIError(
      data.message || `Failed to fetch GLs list: ${response.statusText}`,
      response.status,
      data,
    );
  }

  return data as GetGlsListResponse;
};

/**
 * Delete GL accounts and all associated data
 * @param params Delete parameters (GL IDs and company ID)
 * @returns Delete response with summary
 */
export const deleteGls = async (
  params: DeleteGlsParams,
): Promise<DeleteGlsResponse> => {
  const response = await fetch(`${API_BASE_URL}/api/trial-balance/delete-gls`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      gl_ids: params.gl_ids,
      company_id: getStoredCompanyId(),
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new APIError(
      data.message || `Failed to delete GLs: ${response.statusText}`,
      response.status,
      data,
    );
  }

  return data as DeleteGlsResponse;
};

// Truncate All types
export interface TruncateAllResponse {
  success: boolean;
  message: string;
  data: {
    gls_deleted: number;
    levels_deleted: number;
    tags_deleted: number;
    trial_balance_deleted: number;
    reports_deleted: number;
    registers_deleted: number;
    total_deleted: number;
  };
}

/**
 * Truncate all GL-related data for a company
 * WARNING: This will delete ALL GLs, TB entries, reports, registers, levels, and tags
 * @param companyId Company ID (defaults to stored company)
 * @returns Truncate response with summary
 */
export const truncateAll = async (
  companyId: number = getStoredCompanyId(),
): Promise<TruncateAllResponse> => {
  const response = await fetch(
    `${API_BASE_URL}/api/trial-balance/truncate-all`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        company_id: companyId,
      }),
    },
  );

  const data = await response.json();

  if (!response.ok) {
    throw new APIError(
      data.message || `Failed to truncate all data: ${response.statusText}`,
      response.status,
      data,
    );
  }

  return data as TruncateAllResponse;
};

// Databook View setting (config_number_format id=2): month selection for databook
export type DatabookViewScale =
  | "6months"
  | "yearly"
  | "monthwise"
  | "quarterly";

export interface DatabookViewSettingResponse {
  data?: { scale: string };
  scale?: string;
}

export async function getDatabookViewSetting(): Promise<DatabookViewScale> {
  const response = await fetch(`${API_BASE_URL}/api/settings/databook-view`);
  const body = await response.json();
  const raw = body?.data ?? body ?? {};
  const scale = (raw.scale ?? "monthwise") as string;
  const valid: DatabookViewScale[] = [
    "6months",
    "yearly",
    "monthwise",
    "quarterly",
  ];
  return valid.includes(scale as DatabookViewScale)
    ? (scale as DatabookViewScale)
    : "monthwise";
}

export async function saveDatabookViewSetting(
  scale: DatabookViewScale,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/settings/databook-view`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ scale }),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new APIError(
      data?.message ??
        data?.data?.message ??
        "Failed to save databook view setting",
      response.status,
      data,
    );
  }
}
